import type { DocumentLink, DocumentLinkOrigin, DocumentLinkTargetKind, ExecutionStatus, PlanDocument, ProjectRecord, RecordStatus } from "@/engine/types";
import { recordCode, recordStatusLabel } from "@/store/record-ops";
import { STATUS_LABEL } from "@/store/execution-task-ops";
import { newId } from "@/store/tree-ops";
import { blockSpots, collectMentions, mentionAnchor, replaceMentionWithText } from "./doc-blocks";
import { documentCode } from "./doc-model";

export type LinkCatalog = {
  executionTasks: ReadonlyArray<{ id: string; title: string; status?: ExecutionStatus }>;
  tasks: ReadonlyArray<{ id: string; name: string; wbs?: string; percentComplete?: number }>;
  records: readonly ProjectRecord[];
};

export function linkPairKey(link: Pick<DocumentLink, "documentId" | "targetKind" | "targetId">): string {
  return `${link.documentId}:${link.targetKind}:${link.targetId}`;
}

export function findTarget(catalog: LinkCatalog, kind: DocumentLinkTargetKind, id: string) {
  if (kind === "executionTask") return catalog.executionTasks.find((task) => task.id === id) ?? null;
  if (kind === "scheduleTask") return catalog.tasks.find((task) => task.id === id) ?? null;
  return catalog.records.find((record) => record.id === id) ?? null;
}

export function targetSnapshot(catalog: LinkCatalog, kind: DocumentLinkTargetKind, id: string): { code?: string; title: string } | null {
  const target = findTarget(catalog, kind, id);
  if (!target) return null;
  if (kind === "executionTask") {
    const task = target as LinkCatalog["executionTasks"][number];
    return { title: task.title || "제목 없음" };
  }
  if (kind === "scheduleTask") {
    const task = target as LinkCatalog["tasks"][number];
    return { ...(task.wbs ? { code: task.wbs } : {}), title: task.name || "이름 없는 작업" };
  }
  const record = target as ProjectRecord;
  return { code: recordCode(record), title: record.title || "제목 없음" };
}

export function addExplicitLink(
  links: readonly DocumentLink[],
  input: {
    documentId: string;
    targetKind: DocumentLinkTargetKind;
    targetId: string;
    snapshot: { code?: string; title: string };
    actor: string;
    now: string;
    blockId?: string;
  },
): { links: DocumentLink[]; created: boolean } {
  const key = linkPairKey({ documentId: input.documentId, targetKind: input.targetKind, targetId: input.targetId });
  const existing = links.find((link) => linkPairKey(link) === key);
  if (existing) {
    if (existing.origins.includes("explicit")) return { links: links as DocumentLink[], created: false };
    return {
      links: links.map((link) => (link.id === existing.id ? { ...link, origins: [...link.origins, "explicit" as const] } : link)),
      created: false,
    };
  }
  const link: DocumentLink = {
    id: newId("dl"),
    documentId: input.documentId,
    targetKind: input.targetKind,
    targetId: input.targetId,
    origins: ["explicit"],
    snapshot: input.snapshot,
    createdAt: input.now,
    createdBy: input.actor,
    ...(input.blockId ? { blockId: input.blockId } : {}),
  };
  return { links: [...links, link], created: true };
}

export function removeLink(links: readonly DocumentLink[], linkId: string): DocumentLink[] {
  return links.filter((link) => link.id !== linkId);
}

export function syncMentionLinks(
  links: readonly DocumentLink[],
  documentId: string,
  blocks: Parameters<typeof collectMentions>[0],
  catalog: LinkCatalog,
  actor: string,
  now: string,
): DocumentLink[] {
  const chips = new Set(collectMentions(blocks).map((chip) => `${chip.targetKind}:${chip.targetId}`));
  const next: DocumentLink[] = [];
  for (const link of links) {
    if (link.documentId !== documentId) {
      next.push(link);
      continue;
    }
    const key = `${link.targetKind}:${link.targetId}`;
    const mentioned = chips.has(key);
    if (!mentioned && link.origins.includes("mention")) {
      const origins = link.origins.filter((origin) => origin !== "mention");
      if (!origins.length) continue;
      next.push({ ...link, origins });
      chips.delete(key);
      continue;
    }
    if (mentioned && !link.origins.includes("mention")) {
      next.push(withAnchor({ ...link, origins: [...link.origins, "mention"] }, blocks));
      chips.delete(key);
      continue;
    }
    next.push(mentioned ? withAnchor(link, blocks) : link);
    chips.delete(key);
  }
  for (const key of chips) {
    const split = key.indexOf(":");
    const targetKind = key.slice(0, split) as DocumentLinkTargetKind;
    const targetId = key.slice(split + 1);
    if (targetKind !== "executionTask" && targetKind !== "scheduleTask" && targetKind !== "record") continue;
    const snapshot = targetSnapshot(catalog, targetKind, targetId);
    if (!snapshot) continue;
    const anchor = mentionAnchor(blocks, targetKind, targetId);
    next.push({
      id: newId("dl"),
      documentId,
      targetKind,
      targetId,
      origins: ["mention"],
      snapshot,
      createdAt: now,
      createdBy: actor,
      ...(anchor ? { blockId: anchor } : {}),
    });
  }
  return next;
}

/** 문장 위치는 처음 생긴 블록만 기억한다. 나중 언급이 다른 블록이어도 옮기지 않는다. */
function withAnchor(link: DocumentLink, blocks: Parameters<typeof collectMentions>[0]): DocumentLink {
  if (link.blockId) return link;
  const anchor = mentionAnchor(blocks, link.targetKind, link.targetId);
  return anchor ? { ...link, blockId: anchor } : link;
}

export function markTargetsMissing(
  links: readonly DocumentLink[],
  catalog: LinkCatalog,
  kind: DocumentLinkTargetKind,
  ids: readonly string[],
  now: string,
): DocumentLink[] {
  const gone = new Set(ids);
  if (!gone.size) return [...links];
  return links.map((link) => {
    if (link.targetKind !== kind || !gone.has(link.targetId) || link.missingAt) return link;
    const snapshot = targetSnapshot(catalog, kind, link.targetId) ?? link.snapshot;
    return { ...link, missingAt: now, snapshot };
  });
}

export function supersedeCaption(records: readonly ProjectRecord[], recordId: string): { text: string; successorId: string } | null {
  const current = records.find((record) => record.id === recordId);
  if (!current || current.status !== "superseded") return null;
  const successor = records.find((record) => record.supersedes === recordId);
  if (!successor) return null;
  return { text: `${recordCode(successor)}로 대체됨`, successorId: successor.id };
}

export type DocumentChip = {
  linkId: string;
  targetKind: DocumentLinkTargetKind;
  targetId: string;
  code?: string;
  title: string;
  status?: string;
  missing: boolean;
  supersede?: string;
  successorId?: string;
  executionStatus?: ExecutionStatus;
  recordKind?: ProjectRecord["kind"];
  recordStatus?: RecordStatus;
};

export function presentLink(link: DocumentLink, catalog: LinkCatalog): DocumentChip {
  if (link.missingAt) {
    return {
      linkId: link.id,
      targetKind: link.targetKind,
      targetId: link.targetId,
      ...(link.snapshot.code ? { code: link.snapshot.code } : {}),
      title: link.snapshot.title || "제목 없음",
      status: "삭제됨",
      missing: true,
    };
  }
  const target = findTarget(catalog, link.targetKind, link.targetId);
  if (!target) {
    return {
      linkId: link.id,
      targetKind: link.targetKind,
      targetId: link.targetId,
      ...(link.snapshot.code ? { code: link.snapshot.code } : {}),
      title: link.snapshot.title || "제목 없음",
      status: "삭제됨",
      missing: true,
    };
  }
  if (link.targetKind === "record") {
    const record = target as ProjectRecord;
    const supersede = supersedeCaption(catalog.records, record.id);
    return {
      linkId: link.id,
      targetKind: link.targetKind,
      targetId: link.targetId,
      code: recordCode(record),
      title: record.title || "제목 없음",
      status: recordStatusLabel(record.kind, record.status),
      missing: false,
      recordKind: record.kind,
      recordStatus: record.status,
      ...(supersede ? { supersede: supersede.text, successorId: supersede.successorId } : {}),
    };
  }
  if (link.targetKind === "executionTask") {
    const task = target as LinkCatalog["executionTasks"][number];
    return {
      linkId: link.id,
      targetKind: link.targetKind,
      targetId: link.targetId,
      title: task.title || "제목 없음",
      status: task.status ? STATUS_LABEL[task.status] : "상태 미정",
      missing: false,
      ...(task.status ? { executionStatus: task.status } : {}),
    };
  }
  const task = target as LinkCatalog["tasks"][number];
  return {
    linkId: link.id,
    targetKind: link.targetKind,
    targetId: link.targetId,
    ...(task.wbs ? { code: task.wbs } : {}),
    title: task.name || "이름 없는 작업",
    status: task.percentComplete === 100 ? "완료" : task.percentComplete != null ? `${task.percentComplete}%` : undefined,
    missing: false,
  };
}

export const DOCUMENT_LINK_PREVIEW = 2;

export const LINK_GROUP_LABEL: Record<DocumentLinkTargetKind, string> = {
  executionTask: "작업",
  scheduleTask: "일정",
  record: "이슈",
};

const LINK_GROUP_ORDER: DocumentLinkTargetKind[] = ["executionTask", "scheduleTask", "record"];

export function groupDocumentLinks<T extends { targetKind: DocumentLinkTargetKind }>(links: readonly T[]): Array<{ kind: DocumentLinkTargetKind; label: string; links: T[] }> {
  return LINK_GROUP_ORDER.map((kind) => ({ kind, label: LINK_GROUP_LABEL[kind], links: links.filter((link) => link.targetKind === kind) })).filter((group) => group.links.length > 0);
}

/** 삭제됨 줄도 연결이다. 줄이 하나도 없을 때만 연결 없음이다. */
export function documentIsUnlinked(links: readonly Pick<DocumentLink, "documentId">[], documentId: string): boolean {
  return !links.some((link) => link.documentId === documentId);
}

const SENTENCE_MAX = 80;

/** 링크가 태어난 문장. 블록이 없어지면 언급 블록으로, 그것도 없으면 문장 없이 문서만 보인다. */
export function linkContext(
  blocks: Parameters<typeof blockSpots>[0],
  link: { blockId?: string; targetKind: string; targetId: string },
): { heading?: string; sentence?: string } | null {
  const spots = blockSpots(blocks);
  let index = link.blockId ? spots.findIndex((spot) => spot.id === link.blockId) : -1;
  if (index < 0) index = spots.findIndex((spot) => spot.mentions.some((item) => item.targetKind === link.targetKind && item.targetId === link.targetId));
  if (index < 0) return null;
  const spot = spots[index]!;
  const sentence = clipSentence(spot.text);
  if (spot.type === "heading") return sentence ? { sentence } : null;
  let heading: string | undefined;
  for (let i = index - 1; i >= 0; i -= 1) {
    const prior = spots[i];
    if (prior?.type === "heading" && prior.text.trim()) {
      heading = prior.text.trim();
      break;
    }
  }
  if (!heading && !sentence) return null;
  if (heading && sentence && heading === sentence) return { sentence };
  return { ...(heading ? { heading } : {}), ...(sentence ? { sentence } : {}) };
}

function clipSentence(text: string): string {
  const one = text.replace(/\s+/g, " ").trim();
  if (!one) return "";
  return one.length > SENTENCE_MAX ? `${one.slice(0, SENTENCE_MAX)}…` : one;
}

export function mentionTargetAlive(kind: string, id: string, catalog: LinkCatalog): boolean {
  return liveMentionLabel(kind, id, catalog) !== null;
}

/** 살아 있으면 번호·제목. 없으면 그 문서의 스냅샷과 삭제됨. 평문 번호는 칩이 되지 않는다. */
export function mentionChipLabel(kind: string, id: string, catalog: LinkCatalog, links: readonly DocumentLink[], documentId?: string): string {
  const live = liveMentionLabel(kind, id, catalog);
  if (live) return live;
  const scoped = links.find((link) => link.targetKind === kind && link.targetId === id && (!documentId || link.documentId === documentId));
  const link = scoped ?? links.find((item) => item.targetKind === kind && item.targetId === id);
  if (!link) return "삭제됨";
  const name = [link.snapshot.code, link.snapshot.title].filter(Boolean).join(" ");
  return name ? `${name} 삭제됨` : "삭제됨";
}

function liveMentionLabel(kind: string, id: string, catalog: LinkCatalog): string | null {
  if (kind === "executionTask") {
    const task = catalog.executionTasks.find((item) => item.id === id);
    return task ? task.title || "제목 없음" : null;
  }
  if (kind === "scheduleTask") {
    const task = catalog.tasks.find((item) => item.id === id);
    if (!task) return null;
    return [task.wbs, task.name || "이름 없는 작업"].filter(Boolean).join(" ");
  }
  if (kind === "record") {
    const record = catalog.records.find((item) => item.id === id);
    if (!record) return null;
    return `${recordCode(record)} ${record.title || "제목 없음"}`;
  }
  return null;
}

export function linkCountSummary(links: readonly DocumentLink[]): { count: number; missing: number; tooltip: string } {
  const missing = links.filter((link) => link.missingAt).length;
  const count = links.length;
  const tooltip = missing ? `연결 ${count} · 삭제됨 ${missing}` : `연결 ${count}`;
  return { count, missing, tooltip };
}

export function readableDocumentIdsForTargets(
  documents: readonly PlanDocument[],
  links: readonly DocumentLink[],
  kind: DocumentLinkTargetKind,
  ids: readonly string[],
  canRead: (doc: PlanDocument) => boolean,
): string[] {
  const targets = new Set(ids);
  const docs = new Map(documents.map((doc) => [doc.id, doc]));
  const found = new Set<string>();
  for (const link of links) {
    if (link.targetKind !== kind || !targets.has(link.targetId)) continue;
    const doc = docs.get(link.documentId);
    if (!doc || doc.deletedAt || !canRead(doc)) continue;
    found.add(doc.id);
  }
  return [...found];
}

export function tombstoneSentence(count: number): string | null {
  if (count <= 0) return null;
  return `이 항목을 가리키는 문서가 ${count}개 있습니다. 문서에서는 제목이 삭제됨으로 남습니다.`;
}

export function unlinkAndFlatten(
  doc: PlanDocument,
  links: readonly DocumentLink[],
  linkId: string,
): { doc: PlanDocument; links: DocumentLink[] } | null {
  const link = links.find((item) => item.id === linkId && item.documentId === doc.id);
  if (!link) return null;
  const chip = presentLink(link, { executionTasks: [], tasks: [], records: [] });
  const label = [chip.code, chip.title].filter(Boolean).join(" ");
  return {
    doc: { ...doc, blocks: replaceMentionWithText(doc.blocks, link.targetKind, link.targetId, label) },
    links: removeLink(links, linkId),
  };
}

export function documentsForTarget(documents: readonly PlanDocument[], links: readonly DocumentLink[], kind: DocumentLinkTargetKind, targetId: string, canRead: (doc: PlanDocument) => boolean): PlanDocument[] {
  const ids = new Set(links.filter((link) => link.targetKind === kind && link.targetId === targetId).map((link) => link.documentId));
  return documents
    .filter((doc) => ids.has(doc.id) && !doc.deletedAt && canRead(doc))
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0));
}

export function originLabel(origin: DocumentLinkOrigin): string {
  return origin === "explicit" ? "연결" : "언급";
}

export function documentChipCode(doc: Pick<PlanDocument, "seq">): string {
  return documentCode(doc.seq);
}
